import React, { useState, useEffect } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { calcPriceInclVat, calcPriceExclVat, formatPrice, roundMoney } from '../../utils/vat';
import DecimalField from '../shared/DecimalField';
import { useUserStore } from '../../store';

import { can } from '../../utils/access';
import { formatLaborHours } from '../../utils/laborTime';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';
function EditJobDialog({ open, handleClose, job, maintenanceId, setMaintenance, isMaintenanceInProgress, maintenance }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const canEditPrices = can(currentOrganization, 'work_orders.edit_prices');
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState('');
  const [useDefaultPrice, setUseDefaultPrice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [priceIncludesVat, setPriceIncludesVat] = useState(false);
  const [description, setDescription] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [invoiceTarget, setInvoiceTarget] = useState('insurance');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    if (job) {
      setQuantity(parseFloat(job.quantity) || 1);
      setDescription(job.description || '');
      setDiscountPercent(parseFloat(job.discount_percent) > 0 ? parseFloat(job.discount_percent) : '');
      setDeleteConfirm(false);
      setInvoiceTarget(job.invoice_target || 'insurance');
      if (job.unit_price !== null && job.unit_price !== undefined) {
        // User explicitly overrode the price — respect saved preference
        setUseDefaultPrice(false);
        if (job.price_entered_with_vat && job.unit_price_incl_vat) {
          setPriceIncludesVat(true);
          setUnitPrice(job.unit_price_incl_vat);
        } else {
          setPriceIncludesVat(false);
          setUnitPrice(job.unit_price);
        }
      } else {
        // Using default catalog price — use org default for VAT toggle
        setUseDefaultPrice(true);
        setPriceIncludesVat(orgDefaultVat);
        if (orgDefaultVat) {
          const storedInclVat = job.unit_price_incl_vat || job.job_stored_price_incl_vat;
          if (storedInclVat) {
            setUnitPrice(storedInclVat);
          } else {
            const price = parseFloat(job.default_price || 0);
            const vr = parseFloat(job.vat_rate || 25.5);
            setUnitPrice(calcPriceInclVat(price, vr));
          }
        } else {
          setUnitPrice(job.default_price || '');
        }
      }
    }
  }, [job, orgDefaultVat]);

  useEffect(() => {
    if (open) {
      const loadEmployees = async () => {
        try {
          const response = await axios.get('/api/employees/');
          const data = response.data.results || response.data;
          const list = Array.isArray(data) ? data : [];
          setEmployees(list);
          if (job?.employee_ids?.length > 0) {
            setSelectedEmployees(list.filter(e => job.employee_ids.includes(e.id)));
          } else {
            setSelectedEmployees([]);
          }
        } catch (err) {
          console.error('Error loading employees:', err);
        }
      };
      loadEmployees();
    }
  }, [open, job?.id]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Get VAT rate first (needed for calculations)
      const vatRate = parseFloat(job?.vat_rate || 25.5);

      // Get the entered price value
      const enteredPriceValue = parseFloat(unitPrice) || 0;

      // Calculate VAT-exclusive price for storage
      let priceExclVat = enteredPriceValue;
      let priceInclVat = null;
      const enteredWithVat = !useDefaultPrice && priceIncludesVat && enteredPriceValue !== 0;

      if (enteredWithVat) {
        // User entered VAT-inclusive price - store it as source of truth
        priceInclVat = enteredPriceValue;
        priceExclVat = calcPriceExclVat(enteredPriceValue, vatRate);
      }

      const data = {
        quantity: parseFloat(quantity) || 1,
        unit_price: useDefaultPrice ? null : priceExclVat,
        ...(useDefaultPrice ? {} : {
          unit_price_incl_vat: enteredWithVat ? priceInclVat : null,
          price_entered_with_vat: enteredWithVat,
        }),
        description: description,
        discount_percent: parseFloat(discountPercent) || 0,
        invoice_target: invoiceTarget,
        employee_ids: selectedEmployees.map(e => e.id),
      };
      const response = await axios.patch(
        `/api/maintenances/${maintenanceId}/update_job/${job.id}/`,
        data
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error updating job:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.delete(
        `/api/maintenances/${maintenanceId}/remove_job/${job.id}/`
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error removing job:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.patch(
        `/api/maintenances/${maintenanceId}/toggle_job_status/${job.id}/`
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error toggling job status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDeleteConfirm(false);
    handleClose();
  };

  const handleClearDeclined = async () => {
    setLoading(true);
    try {
      const response = await axios.patch(
        `/api/maintenances/${maintenanceId}/update_job/${job.id}/`,
        { confirmation_status: 'none' }
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      console.error('Error clearing declined status:', err);
    } finally {
      setLoading(false);
    }
  };

  const enteredPrice = useDefaultPrice ? parseFloat(job?.default_price || 0) : parseFloat(unitPrice || 0);
  const vatRate = parseFloat(job?.vat_rate || 25.5);
  const discountMultiplier = 1 - (parseFloat(discountPercent) || 0) / 100;
  // Calculate display values based on whether price includes VAT (only applies when not using default price)
  const showVatInclusive = !useDefaultPrice && priceIncludesVat;
  const effectivePriceExclVat = showVatInclusive ? calcPriceExclVat(enteredPrice, vatRate) : enteredPrice;
  const storedDefaultInclVat = useDefaultPrice
    ? parseFloat(job?.unit_price_incl_vat || job?.job_stored_price_incl_vat || 0)
    : 0;
  const effectivePriceInclVat = showVatInclusive
    ? enteredPrice
    : (storedDefaultInclVat ? storedDefaultInclVat : calcPriceInclVat(enteredPrice, vatRate));
  const lineTotalExclVat = roundMoney(quantity * effectivePriceExclVat * discountMultiplier);
  const lineTotalInclVat = (parseFloat(discountPercent) || 0) > 0
    ? roundMoney(lineTotalExclVat * (1 + vatRate / 100))
    : roundMoney(quantity * effectivePriceInclVat);
  const vatAmount = roundMoney(lineTotalInclVat - lineTotalExclVat);

  const getPriceTypeLabel = (type) => {
    switch (type) {
      case 'hourly': return t('Hourly rate');
      case 'fixed': return t('Fixed price');
      case 'variable': return t('Variable');
      default: return type;
    }
  };

  const getQuantityLabel = () => {
    if (job?.price_type === 'hourly') {
      return t('Hours');
    }
    return t('Quantity');
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {t('Edit job')}
        </Typography>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Job Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
              {job.name}
            </Typography>
            <Chip
              label={job.is_completed ? t('Completed') : t('Pending')}
              size="small"
              icon={job.is_completed ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
              sx={{
                bgcolor: job.is_completed ? 'success.light' : 'action.selected',
                color: job.is_completed ? 'success.dark' : 'text.secondary',
                '& .MuiChip-icon': {
                  color: job.is_completed ? 'success.dark' : 'text.secondary',
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={getPriceTypeLabel(job.price_type)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '11px' }}
            />
            <Chip
              label={`${t('VAT')} ${formatPrice(vatRate, 1)} %`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '11px' }}
            />
            {job.estimated_duration && (
              <Chip
                icon={<AccessTimeIcon />}
                label={t('laborTime.hours', { hours: formatLaborHours(job.estimated_duration) })}
                size="small"
                variant="outlined"
                sx={{ fontSize: '11px' }}
              />
            )}
            {job.ai_estimation && (
              <Chip
                icon={<AutoFixHighIcon />}
                label={t('AI estimate')}
                size="small"
                sx={{
                  bgcolor: 'info.light',
                  color: 'info.dark',
                  '& .MuiChip-icon': { color: 'info.dark' },
                  fontSize: '11px',
                }}
              />
            )}
          </Box>
        </Box>

        {/* AI Work Steps */}
        {job.ai_estimation && job.ai_estimation.sub_tasks && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('AI work steps')}
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.04)', borderRadius: '8px', border: '1px solid', borderColor: 'info.light' }}>
              {job.ai_estimation.sub_tasks.map((step, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', minWidth: '20px' }}>
                    {idx + 1}.
                  </Typography>
                  <Typography sx={{ fontSize: '13px' }}>
                    {step}
                  </Typography>
                </Box>
              ))}
              {job.ai_estimation.confidence && (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={`${t('Confidence')}: ${t(job.ai_estimation.confidence)}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '11px' }}
                  />
                </Box>
              )}
              {job.ai_estimation.notes && (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                  {job.ai_estimation.notes}
                </Typography>
              )}
              {job.ai_estimation.multiplier && job.ai_estimation.multiplier !== 1 && (
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    label={`${t('AI estimate multiplier')}: ${job.ai_estimation.original_estimated_hours} h × ${job.ai_estimation.multiplier} = ${job.ai_estimation.estimated_hours} h`}
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ fontSize: '11px' }}
                  />
                </Box>
              )}
            </Box>
            {(!currentOrganization?.ai_estimate_default_job || !job.ai_estimation.multiplier || job.ai_estimation.multiplier === 1) && (
              <Alert severity="info" sx={{ mt: 1.5, fontSize: '12px', py: 0.5 }}>
                {!currentOrganization?.ai_estimate_default_job && <Box>{t('AI estimate no job configured')}</Box>}
                {(!job.ai_estimation.multiplier || job.ai_estimation.multiplier === 1) && <Box>{t('AI estimate no multiplier configured')}</Box>}
              </Alert>
            )}
          </Box>
        )}

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Description')}
          </Typography>
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            placeholder={t('Describe what work was done...')}
            fullWidth
            size="small"
            inputProps={{ maxLength: 500 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
            {t('Optional. Shows on invoice if filled.')} ({description.length}/500)
          </Typography>
        </Box>

        {/* Assign mechanics */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Assigned mechanics')}
          </Typography>
          <Autocomplete
            multiple
            options={employees}
            getOptionLabel={(option) => option?.name || ''}
            value={selectedEmployees}
            onChange={(e, value) => setSelectedEmployees(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '11px',
                    bgcolor: 'primary.main',
                    mr: 1,
                  }}
                >
                  {option.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </Avatar>
                <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main', width: 22, height: 22, fontSize: '10px' }}>
                      {option.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </Avatar>
                  }
                  label={option.name}
                  size="small"
                  sx={{ borderRadius: '8px' }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                placeholder={selectedEmployees.length === 0 ? t('Search mechanics...') : ''}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            )}
          />
        </Box>

        {/* Invoice target toggle. In ALV mode it controls insurance coverage
            (covered line VAT goes to the leasing company, so "Korvattava/Ei korvattava"). */}
        {maintenance?.billing_customer && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Invoice target')}
            </Typography>
            <ToggleButtonGroup
              value={invoiceTarget}
              exclusive
              onChange={(e, val) => { if (val) setInvoiceTarget(val); }}
              size="small"
              fullWidth
            >
              <ToggleButton value="insurance" sx={{ textTransform: 'none', fontSize: '13px' }}>
                {maintenance?.insurance_vat_deductible
                  ? t('Covered')
                  : (maintenance?.billing_customer_is_insurance ? t('Insurance') : t('Billing customer'))}
              </ToggleButton>
              <ToggleButton value="customer" sx={{ textTransform: 'none', fontSize: '13px' }}>
                {maintenance?.insurance_vat_deductible ? t('Not covered') : t('Customer')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Customer Declined Banner */}
        {job.confirmation_status === 'denied' && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'error.lighter',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
              <Typography sx={{ fontSize: '14px', color: 'error.dark', fontWeight: 500 }}>
                {t('Customer declined')}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearDeclined}
              disabled={loading}
              sx={{
                borderColor: 'error.main',
                color: 'error.main',
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '12px',
                '&:hover': {
                  borderColor: 'error.dark',
                  bgcolor: 'error.lighter',
                },
              }}
            >
              {t('Clear status')}
            </Button>
          </Box>
        )}

        {/* Toggle Status Button (only when maintenance is in progress) */}
        {isMaintenanceInProgress && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant={job.is_completed ? 'outlined' : 'contained'}
              startIcon={job.is_completed ? <RadioButtonUncheckedIcon /> : <CheckCircleIcon />}
              onClick={handleToggleStatus}
              disabled={loading}
              fullWidth
              sx={{
                borderRadius: '8px',
                py: 1.5,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                bgcolor: job.is_completed ? 'transparent' : 'success.main',
                borderColor: job.is_completed ? 'divider' : 'success.main',
                color: job.is_completed ? 'text.primary' : 'white',
                '&:hover': {
                  bgcolor: job.is_completed ? 'action.hover' : 'success.dark',
                },
              }}
            >
              {job.is_completed ? t('Mark as pending') : t('Mark as completed')}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Quantity & Discount */}
        <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {getQuantityLabel()}
            </Typography>
            <DecimalField
              value={quantity}
              onChange={setQuantity}
              placeholder="1"
              size="small"
              sx={{
                width: '120px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
          {!maintenance?.billing_customer_is_insurance && (
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                {t('Discount %')}
              </Typography>
              <DecimalField
                value={discountPercent}
                disabled={!canEditPrices}
                onChange={(val) => {
                  const num = parseFloat(val);
                  if (!isNaN(num) && num > 100) return;
                  setDiscountPercent(val);
                }}
                size="small"
                placeholder="0"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{
                  width: '100px',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Price */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {job.price_type === 'hourly'
              ? (priceIncludesVat ? t('Hourly rate (incl. VAT)') : t('Hourly rate (excl. VAT)'))
              : (priceIncludesVat ? t('Unit price (incl. VAT)') : t('Unit price (excl. VAT)'))}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DecimalField
                value={unitPrice}
                disabled={!canEditPrices}
                onChange={(value) => {
                  setUnitPrice(value);
                  setUseDefaultPrice(false);
                }}
                allowNegative
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{
                  width: '150px',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
              <VatModeToggle
                value={priceIncludesVat ? 'incl' : 'excl'}
                onChange={(e, val) => {
                  if (val) {
                    setPriceIncludesVat(val === 'incl');
                    setUseDefaultPrice(false);  // Toggle change is a manual price decision
                  }
                }}
                sx={{ flexShrink: 0 }}
              />
              {!useDefaultPrice && (
                <Button
                  size="small"
                  onClick={() => {
                    // Reset to default catalog price — use org default for VAT toggle
                    setPriceIncludesVat(orgDefaultVat);
                    if (orgDefaultVat) {
                      const storedInclVat = job.unit_price_incl_vat || job.job_stored_price_incl_vat;
                      if (storedInclVat) {
                        setUnitPrice(storedInclVat);
                      } else {
                        const price = parseFloat(job.default_price || 0);
                        const vr = parseFloat(job.vat_rate || 25.5);
                        setUnitPrice(calcPriceInclVat(price, vr));
                      }
                    } else {
                      setUnitPrice(job.default_price || '');
                    }
                    setUseDefaultPrice(true);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Reset to default')}
                </Button>
              )}
            </Box>
            {/* Show calculated price */}
            <VatConverseLine value={unitPrice} mode={priceIncludesVat ? 'incl' : 'excl'} vatRate={vatRate} />
            {parseFloat(unitPrice) < 0 && (
              <Typography sx={{ fontSize: '12px', color: 'warning.main' }}>
                {t('negativePriceHint')}
              </Typography>
            )}
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {t('Default price')}: {job.default_price} €
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Line Total with VAT breakdown */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {t('Subtotal (excl. VAT)')}
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {formatPrice(lineTotalExclVat)} €
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {t('VAT')} ({formatPrice(vatRate, 1)} %)
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {formatPrice(vatAmount)} €
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {t('Total (incl. VAT)')}
            </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
              {formatPrice(lineTotalInclVat)} €
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          disabled={loading}
          sx={{
            borderRadius: '8px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {deleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCancel}
            sx={{
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              bgcolor: 'secondary.main',
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditJobDialog;
